import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Microscope, CheckCircle2, AlertTriangle, ShieldCheck, TrendingUp, Lightbulb, XCircle, FlaskConical, ArrowUpRight, ArrowDownRight, GitCompare, Send, Loader2, Clock, Activity, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// One evidence-backed recommendation (Phase D). Material recs expose a
// "Route to approval" action that goes through the existing four-eyes gateway.
function RecommendationRow({ rec, idx }) {
  const [state, setState] = useState(rec.approval_status === 'pending_approval' ? 'submitted' : 'idle');
  const [approvalId, setApprovalId] = useState(rec.approval_id || null);
  const navigate = useNavigate();
  const submit = async () => {
    setState('submitting');
    try {
      const res = await api.submitForApproval(rec.rec_id);
      if (res?.approval_id) setApprovalId(res.approval_id);
      setState('submitted');
      toast.success('Routed to approval — pending a named approver (four-eyes).');
    } catch (e) {
      // 409 = an approval is already open for this rule. The backend echoes the
      // existing approval_id so the deep-link still resolves to the right item.
      if (e.status === 409 || (e.message || '').includes('already exists')) {
        const existingId = (e.detail && typeof e.detail === 'object' && e.detail.approval_id) || null;
        if (existingId) setApprovalId(existingId);
        setState('submitted');
        toast.info('An approval is already open for this recommendation.');
      } else { setState('idle'); toast.error(e.message || 'Could not route to approval'); }
    }
  };
  const urgencyTone = { high: 'text-rose-600 dark:text-rose-400', medium: 'text-amber-600 dark:text-amber-400', low: 'text-muted-foreground' }[rec.urgency] || 'text-muted-foreground';
  const chip = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] border border-border/40 bg-muted/30';
  return (
    <div className="p-2 rounded-lg border border-border/40 bg-background/50" data-testid={`diagnostic-rec-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
            {rec.title}
            {rec.material && <span className="text-[8px] px-1 py-0.5 rounded bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/30">material</span>}
          </p>
          <p className="text-[10px] text-foreground/70 mt-0.5">{rec.action}</p>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1 italic">{rec.rationale}</p>
      <div className="flex flex-wrap gap-1 mt-1.5">
        <span className={chip} title="Estimated impact if actioned">💥 {rec.expected_impact}</span>
        <span className={chip}>{rec.category}</span>
        <span className={chip}>effort: {rec.effort}</span>
        <span className={`${chip} ${urgencyTone}`}>urgency: {rec.urgency}</span>
      </div>
      {rec.risk && <p className="text-[9px] text-muted-foreground mt-1"><AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />{rec.risk}</p>}
      {rec.material && (
        <div className="flex items-center gap-2 mt-1.5">
          <button
            onClick={submit}
            disabled={state !== 'idle'}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] border ${state === 'submitted' ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10' : 'border-violet-500/40 text-violet-700 dark:text-violet-300 hover:bg-violet-500/10'}`}
            data-testid={`diagnostic-rec-approve-${idx}`}>
            {state === 'submitting' ? <Loader2 className="w-3 h-3 animate-spin" /> : state === 'submitted' ? <CheckCircle2 className="w-3 h-3" /> : <Send className="w-3 h-3" />}
            {state === 'submitted' ? 'Pending approval' : 'Route to approval'}
          </button>
          {state === 'submitted' && (
            <button
              onClick={() => navigate(`/admin/recommendation-approvals${approvalId ? `?approval=${approvalId}` : ''}`)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/40"
              data-testid={`diagnostic-rec-view-approval-${idx}`}>
              <ExternalLink className="w-3 h-3" /> View in approvals
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Governed diagnostic answer card (Trusted-AI Phase A + B, C + D). Renders the
// deterministic driver analysis and — when present — a real period-over-period
// move, evidence-backed recommendations, and a disclosed forecast. All numbers
// come from the backend findings (already validated); this never computes one.
export default function DiagnosticCard({ result }) {
  const d = result?.diagnostic;
  if (!d) return null;
  const v = result?.claim_validation || {};
  const pd = d.primary_driver;
  const fs = d.frequency_severity;
  const seg = (d.segment_analysis || [])[0];
  const sections = result?.sections || [];
  const wc = d.what_changed || {};
  const changed = wc.status === 'changed';
  const topRecs = result?.recommendations || [];
  const hasPrescriptiveSection = sections.some((s) => s.kind === 'prescriptive');

  const validated = v.status === 'validated';
  const strengthTone = { strong: 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
    moderate: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30',
    limited: 'text-muted-foreground bg-muted/30 border-border/40' }[d.evidence_strength] || 'bg-muted/30 border-border/40 text-muted-foreground';
  const chip = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border';

  return (
    <div className="mb-2 rounded-xl border border-violet-500/30 bg-violet-500/[0.04] p-3" data-testid="diagnostic-card">
      {/* header */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-700 dark:text-violet-300">
          <Microscope className="w-3.5 h-3.5" /> Governed diagnosis
        </span>
        <span className={`${chip} ${strengthTone}`} title="How strong the evidence behind this diagnosis is" data-testid="diagnostic-strength">
          <ShieldCheck className="w-3 h-3" /> {d.evidence_strength} evidence
        </span>
        <span className={`${chip} ${validated ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30' : 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30'}`}
          title={validated ? `Every number in this answer was checked against a finding (${v.supported_count}/${v.checked}).`
            : `${(v.unsupported || []).length} number(s) could not be tied to a finding and were flagged.`}
          data-testid="diagnostic-validation">
          {validated ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
          {validated ? `Claims validated (${v.supported_count}/${v.checked})` : `Claims qualified (${v.supported_count}/${v.checked})`}
        </span>
      </div>

      {/* what changed */}
      {wc.note && (
        <div className="mb-2 p-2 rounded-lg border border-violet-500/20 bg-background/40" data-testid="diagnostic-what-changed">
          {changed && (
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5 flex items-center gap-1">
              {wc.direction === 'rose' ? <ArrowUpRight className="w-3 h-3 text-rose-500" /> : <ArrowDownRight className="w-3 h-3 text-emerald-500" />}
              What changed
            </p>
          )}
          <p className="text-[11px] text-foreground/80">{wc.note}</p>
          {changed && (
            <div className="flex items-center gap-2 mt-1 text-[11px]">
              <span className="text-muted-foreground">{wc.from_display} <span className="opacity-60">({wc.from_period})</span></span>
              <span>→</span>
              <span className="font-semibold">{wc.to_display} <span className="opacity-60 font-normal">({wc.to_period})</span></span>
              <span className={`font-semibold ${wc.direction === 'rose' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{wc.delta_display}</span>
            </div>
          )}
        </div>
      )}

      {/* primary driver */}
      {pd && (
        <div className="mb-2 p-2 rounded-lg border border-violet-500/20 bg-background/40" data-testid="diagnostic-primary-driver">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5">Primary driver · by {pd.dimension_label}</p>
          <p className="text-xs text-foreground">
            <span className="font-semibold">{pd.leader?.label}</span> at <span className="font-semibold">{pd.leader?.display}</span>
            {' '}<span className="text-violet-700 dark:text-violet-300">({pd.leader?.contribution_display}{changed ? ' of the move' : ' vs the book'})</span>
          </p>
        </div>
      )}

      {/* frequency vs severity */}
      {fs && (
        <div className="mb-2 grid grid-cols-2 gap-2" data-testid="diagnostic-freq-sev">
          <div className="p-2 rounded-lg border border-border/40 bg-background/40">
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Frequency {fs.freq_change ? `(${fs.freq_change})` : ''}</p>
            <p className="text-xs font-semibold">{fs.frequency_display}</p>
          </div>
          <div className="p-2 rounded-lg border border-border/40 bg-background/40">
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Severity {fs.sev_change ? `(${fs.sev_change})` : ''} · {fs.claims_display} claims</p>
            <p className="text-xs font-semibold">{fs.severity_display}</p>
          </div>
        </div>
      )}

      {/* segment contribution */}
      {seg && (seg.segments || []).length > 0 && (
        <div className="mb-2" data-testid="diagnostic-segments">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
            {changed ? 'Contribution to the change' : 'Segment contribution'} · by {seg.dimension_label}
          </p>
          <div className="space-y-1">
            {(seg.segments || []).slice(0, 6).map((s, i) => {
              const up = (s.contribution || 0) > 0;
              return (
                <div key={i} className="flex items-center justify-between text-[11px]" data-testid={`diagnostic-segment-${i}`}>
                  <span className="text-foreground/80">{s.label}</span>
                  <span className="flex items-center gap-2">
                    <span className="tabular-nums text-muted-foreground">{s.display}</span>
                    <span className={`tabular-nums font-medium ${up ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{s.contribution_display}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* tested / ruled out */}
      <div className="flex flex-wrap gap-1 mb-2" data-testid="diagnostic-tests">
        {(d.tested || []).map((t, i) => (
          <span key={`t${i}`} className={`${chip} text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30`}>
            <FlaskConical className="w-3 h-3" /> tested: {t}
          </span>
        ))}
        {(d.ruled_out_labels || []).map((t, i) => (
          <span key={`r${i}`} className={`${chip} text-muted-foreground bg-muted/30 border-border/40`}>
            <XCircle className="w-3 h-3" /> ruled out: {t}
          </span>
        ))}
      </div>

      {/* limitations + next step */}
      {(d.limitations || []).length > 0 && (
        <p className="text-[10px] text-amber-700 dark:text-amber-300 mb-1" data-testid="diagnostic-limitations">
          <AlertTriangle className="w-3 h-3 inline mr-1" />{d.limitations[0]}
        </p>
      )}
      {d.next_step && (
        <p className="text-[10px] text-muted-foreground" data-testid="diagnostic-next-step">Next step: {d.next_step}</p>
      )}

      {/* Phase D — evidence-backed recommendations (shown here when not already in a compound section) */}
      {topRecs.length > 0 && !hasPrescriptiveSection && (
        <div className="mt-3 border-t border-border/40 pt-2" data-testid="diagnostic-recommendations">
          <p className="text-[10px] uppercase tracking-wider font-semibold mb-1 flex items-center gap-1">
            <Lightbulb className="w-3 h-3 text-amber-500" /> Recommended actions (advisory)
          </p>
          <div className="space-y-1.5">
            {topRecs.map((r, i) => <RecommendationRow key={r.rec_id || i} rec={r} idx={i} />)}
          </div>
          <p className="text-[9px] text-muted-foreground mt-1 italic">Advisory only — nothing auto-executes; material items route to named human approval.</p>
        </div>
      )}

      {/* compound sections (predictive + prescriptive) */}
      {sections.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-border/40 pt-2" data-testid="diagnostic-sections">
          {sections.map((s, i) => (
            <div key={i} className="p-2 rounded-lg border border-border/40 bg-background/40" data-testid={`diagnostic-section-${s.kind}`}>
              <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5 flex items-center gap-1">
                {s.kind === 'predictive' ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <Lightbulb className="w-3 h-3 text-amber-500" />}
                {s.label}
              </p>
              {s.answer && <p className="text-[11px] text-foreground/80">{s.answer}</p>}

              {/* predictive: horizon + uncertainty band + drift (Phase C) */}
              {s.kind === 'predictive' && (
                <div className="flex flex-wrap gap-1 mt-1" data-testid="predictive-disclosure">
                  {s.horizon && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] border border-border/40 bg-muted/30"><Clock className="w-2.5 h-2.5" /> horizon: {s.horizon} {s.unit}</span>}
                  {s.band?.pct != null && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] border border-border/40 bg-muted/30"><GitCompare className="w-2.5 h-2.5" /> ±{s.band.pct}% band</span>}
                  {s.model ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">model: {s.model.name} v{s.model.version}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">no approved model · trend only</span>
                  )}
                  {s.drift?.status && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] border ${s.drift.status === 'critical' ? 'border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400' : s.drift.status === 'drifting' ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300' : 'border-border/40 bg-muted/30'}`}><Activity className="w-2.5 h-2.5" /> drift: {s.drift.status}</span>}
                </div>
              )}

              {/* prescriptive: evidence-backed recommendations with route-to-approval */}
              {s.kind === 'prescriptive' && (s.recommendations || []).length > 0 && (
                <div className="space-y-1.5 mt-1">
                  {(s.recommendations || []).map((r, j) => (
                    r.rec_id ? <RecommendationRow key={r.rec_id} rec={r} idx={j} /> : (
                      <div key={j} className="text-[11px] text-foreground/80">• {r.action} <span className="text-muted-foreground">({r.category}{r.material ? ' · material — needs approval' : ''})</span></div>
                    )
                  ))}
                </div>
              )}
              {s.note && <p className="text-[10px] text-muted-foreground mt-0.5">{s.note}</p>}
              {s.disclosure && <p className="text-[9px] text-muted-foreground mt-1 italic">{s.disclosure}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
